import {
  Button,
  Checkbox,
  FilterListBox,
  Flex,
  Heading,
  IllustratedMessage,
  ListBox,
  Progress,
  Stack,
  TextField,
  TimeField,
} from '@eleveo/components-react';
import { COLOR_BLACK, COLOR_WHITE } from '@eleveo/foundation';
import { CircleIcon, LockIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { Time } from '@internationalized/date';
import clsx from 'clsx';
import { equals } from 'ramda';
import React, { useId, useMemo, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { DISABLED_PARAMETERS_KEYS, MIN_MINUTES_STEP, PARAMETERS_KEYS_SORTED, parametersFieldsNameKeys } from './config';
import { SCHEDULE_ELEMENT_INPUT_FIELD, SCHEDULE_ELEMENT_MESSAGE } from './constants';
import { mapKeysToParameters } from './utils';
import { Center } from '../../components/LayoutNext/components/Center';
import { max255chars, required } from '../../components/MySchedule/validations';
import { composeValidators } from '../../utils/validations/composeValidators';
import { requiredWithoutOtherFields } from '../../utils/validations/requiredWithoutOtherFields';

import type { ScheduleElementValues } from './types';
import type { QueuesTagsResult } from '../../graphql/types';

type ScheduleElementFormProps = {
  isEditable?: boolean;
  isDefaultElement?: boolean;
  tagsResult: QueuesTagsResult;
};

export const ScheduleElementForm: React.FC<ScheduleElementFormProps> = ({
  isEditable = true,
  tagsResult,
  isDefaultElement = false,
}) => {
  const { t } = useTranslation();

  const [isColorPickerVisible, setColorPickerVisible] = useState(false);

  const colorFieldRef = useRef<HTMLInputElement>(null);

  const nameField = useField<ScheduleElementValues['name']>('name', {
    validate: composeValidators(required, max255chars),
  });
  const colorField = useField<ScheduleElementValues['color']>('color', { validate: required });
  const isWholeDayElementField = useField<ScheduleElementValues['isWholeDayElement']>('isWholeDayElement');
  const defaultDurationField = useField<ScheduleElementValues['defaultDuration']>('defaultDuration', {
    validate: requiredWithoutOtherFields([['isWholeDayElement']]),
  });
  const parametersField = useField<ScheduleElementValues['parameters']>('parameters', { isEqual: equals });
  const tagsField = useField<ScheduleElementValues['tags']>('tags');

  const parametersItems = useMemo(
    () =>
      PARAMETERS_KEYS_SORTED.map((key) => ({
        id: key,
        name: t(parametersFieldsNameKeys[key]),
      })),
    [t]
  );
  const allParameterIds = useMemo(() => parametersItems.map((i) => i.id), [parametersItems]);

  const selectedParametersKeys = useMemo(
    () =>
      Object.keys(parametersField.input.value).filter((key) =>
        Boolean(parametersField.input.value[key as keyof ScheduleElementValues['parameters']])
      ),
    [parametersField.input.value]
  );

  const tagsTitleId = useId();
  const parametersTitleId = useId();

  const tagsItems = useMemo(
    () => (tagsResult?.data?.queuesTags ?? []).map(({ id, value }) => ({ id, name: value })),
    [tagsResult?.data?.queuesTags]
  );

  const tagsItemsIds = useMemo(() => tagsItems.map(({ id }) => id), [tagsItems]);

  const tagsEnabled = parametersField.input.value.productive;

  // Default elements: only color + (duration OR whole-day) remain editable. Name, parameters, tags stay read-only.
  // We therefore keep the ancillary fields flag for name / parameters / tags, but allow whole-day toggle participation
  // in the coupling logic with duration for all editable elements (default or not).
  const canEditAncillaryFields = isEditable && !isDefaultElement; // excludes name / parameters / tags when default
  // Even if isEditable is false, for default elements we still allow adjusting color + whole-day + duration.
  const allowCoreEditsForDefault = isDefaultElement; // color / duration / whole-day always allowed for defaults
  // Limited edit mode for non-default locked elements (isEditable=false): allow name, color, duration, whole-day and
  // selected parameter flags (showInOverview / showInOverviewBig / pinnable) but nothing else (tags remain locked)
  const allowLimitedEditForLockedNonDefault = !isEditable && !isDefaultElement;
  const canEditCore = isEditable || allowCoreEditsForDefault || allowLimitedEditForLockedNonDefault;

  return (
    <Stack direction="vertical" className="p-20 w-full" spacing={20}>
      <TextField
        {...nameField.input}
        className="w-full"
        label={t('form.name')}
        testId={`${SCHEDULE_ELEMENT_INPUT_FIELD}--name`}
        isDisabled={!(canEditAncillaryFields || allowLimitedEditForLockedNonDefault)}
        validationState={
          (nameField.meta.touched && nameField.meta.error) ||
          (nameField.meta.submitError && !nameField.meta.dirtySinceLastSubmit)
            ? 'invalid'
            : undefined
        }
      />

      <Stack spacing={20}>
        <Button
          isSelected={isColorPickerVisible}
          onClick={() => {
            setColorPickerVisible(true);
            colorFieldRef?.current?.click();
          }}
          onFocus={(e) => colorField.input.onFocus(e as React.FocusEvent<HTMLElement>)}
          onBlur={(e) => {
            setColorPickerVisible(false);
            colorField.input.onBlur(e as React.FocusEvent<HTMLElement>);
          }}
          testId={`${SCHEDULE_ELEMENT_INPUT_FIELD}--color`}
          variant="outline"
          color={colorField.meta.touched && colorField.meta.error ? 'error' : undefined}
        >
          <Flex>
            <CircleIcon
              style={{
                color: colorField.input.value || 'transparent',
                stroke: isColorPickerVisible ? COLOR_WHITE : COLOR_BLACK,
                strokeWidth: 1,
              }}
            />

            <input
              aria-invalid={colorField.meta.touched && colorField.meta.error}
              className="schedule-elements__form__color-picker-field"
              data-test-id={`${SCHEDULE_ELEMENT_INPUT_FIELD}--color-input`}
              id="color-picker"
              onBlur={() => setColorPickerVisible(false)}
              onChange={(event) => colorField.input.onChange(event.target.value)}
              onFocus={() => setColorPickerVisible(true)}
              ref={colorFieldRef}
              tabIndex={-1}
              type="color"
              value={colorField.input.value}
            />
          </Flex>

          {t('scheduleElements.color')}
        </Button>

        <Stack className="w-full" direction="vertical">
          <TimeField
            {...defaultDurationField.input}
            className="w-full"
            hourCycle={24}
            // Coupled: when whole-day is selected
            // Disabled when whole-day OR (core not editable in any allowed mode)
            isDisabled={isWholeDayElementField.input.value || !canEditCore}
            label={t('scheduleElements.defaultDuration')}
            maxValue={new Time(23, 45)}
            minValue={new Time(0, MIN_MINUTES_STEP)}
            steps={{ minute: MIN_MINUTES_STEP }}
            testId={`${SCHEDULE_ELEMENT_INPUT_FIELD}--default-duration`}
            value={isWholeDayElementField.input.value ? undefined : defaultDurationField.input.value}
            validationState={
              defaultDurationField.meta.touched &&
              defaultDurationField.meta.invalid &&
              !isWholeDayElementField.input.value
                ? 'invalid'
                : undefined
            }
          />

          <Checkbox
            {...isWholeDayElementField.input}
            isSelected={isWholeDayElementField.input.value}
            value={undefined}
            // Whole-day toggle editable if any core edit mode applies
            isDisabled={!canEditCore}
            testId={`${SCHEDULE_ELEMENT_INPUT_FIELD}--whole-day-element`}
          >
            {t('scheduleElements.wholeDayElement')}
          </Checkbox>
        </Stack>
      </Stack>

      <Heading as="h3" className="text-blue-700" id={parametersTitleId}>
        {t('scheduleElements.elementParameters')}
      </Heading>

      <ListBox
        aria-labelledby={parametersTitleId}
        className={clsx('schedule-elements__form__parameters -m-10 p-10', {
          'schedule-elements__form__parameters--error':
            parametersField.meta.submitError && !parametersField.meta.dirtySinceLastSubmit,
        })}
        items={parametersItems}
        selectionMode="multiple"
        selectedKeys={selectedParametersKeys}
        disabledKeys={(() => {
          if (isDefaultElement) {
            return allParameterIds; // all locked for defaults
          }
          if (canEditAncillaryFields) {
            return undefined; // fully editable
          }
          if (allowLimitedEditForLockedNonDefault) {
            // lock only disallowed subset; permit overview + pinnable
            return DISABLED_PARAMETERS_KEYS; // contains paid, paidTimeOff, productive, shift, sick, vacation
          }
          return DISABLED_PARAMETERS_KEYS;
        })()}
        onSelectionChange={(values) => {
          if (isDefaultElement) {
            return; // fully locked
          }
          if (canEditAncillaryFields || allowLimitedEditForLockedNonDefault) {
            const newParametersValue = mapKeysToParameters(
              Array.from(values) as Array<keyof ScheduleElementValues['parameters']>
            );
            // In limited mode productive is always false (disabled), so no tag reset logic needed based on user toggle
            if (!newParametersValue.productive && (tagsField.input.value?.length ?? 0) > 0) {
              tagsField.input.onChange([]);
            }
            parametersField.input.onChange(newParametersValue);
          }
        }}
        testId={`${SCHEDULE_ELEMENT_INPUT_FIELD}--parameters`}
      />

      <Heading as="h3" className="text-blue-700" id={tagsTitleId}>
        {t('scheduleElements.contributesToTag')}
      </Heading>

      {tagsEnabled ? (
        <>
          {tagsResult.loading ? (
            <Center className="w-full">
              <Progress />
            </Center>
          ) : (
            <FilterListBox
              aria-labelledby={tagsTitleId}
              disabledKeys={canEditAncillaryFields ? undefined : tagsItemsIds}
              items={tagsItems}
              onSelectionChange={(values) => {
                if (!canEditAncillaryFields) {
                  return;
                }
                // only one tag allowed -> store as array (0 or 1)
                const first = values.values().next().value;
                tagsField.input.onChange(first ? [first] : []);
              }}
              selectedKeys={tagsField.input.value && tagsField.input.value.length > 0 ? [tagsField.input.value[0]] : []}
              selectionMode="single"
              testId={`${SCHEDULE_ELEMENT_INPUT_FIELD}--contributes-to-tag`}
            />
          )}
        </>
      ) : (
        <IllustratedMessage
          className="-mt-10"
          description={t('scheduleElements.selectProductiveToUnlockTagContribution')}
          heading={t('scheduleElements.tagContributionIsLocked')}
          icon={<LockIcon />}
          testId={`${SCHEDULE_ELEMENT_MESSAGE}--tags-not-enabled-until-productive-got-enabled`}
        />
      )}
    </Stack>
  );
};
